import config from "@Config/index";
import { extractLanguageFromPath, addLanguageToPath, hasLanguagePrefix } from "@Utils/languageCore";

function getCurrentLanguageFromUrl(): string {
  if (typeof window === "undefined") return "en";
  const { pathname } = window.location;
  return extractLanguageFromPath(pathname) || "en";
}
function addLocaleToInternalPath(path: string, language: string): string {
  // Don't add prefix for English or Chinese (Chinese uses .cn domain)
  if (language === "en" || language === "zh") return path;
  if (hasLanguagePrefix(path)) return path;

  return addLanguageToPath(path, language);
}

/** CMS sometimes inserts a space after '<': "< a href" / "< strong>" so tags render as text. */
function normalizeCmsHtmlTags(html: string): string {
  return html.replace(/<\s+(?=\/?[a-zA-Z])/g, "<");
}

function replaceAmberDomainInLinks(htmlContent: string): string {
  if (!htmlContent) return "";

  const html = normalizeCmsHtmlTags(htmlContent);
  const currentLanguage = getCurrentLanguageFromUrl();

  // ============ China app: replace domain from .com to .cn==========
  if (config.IS_CHINA_APP) {
    try {
      // CMS often stores escaped quotes: href=\"https://amberstudent.com/...\"
      // or href=\\"https://amberstudent.com/...\\". The browser then treats the
      // quoted URL as a relative path → amberstudent.cn/%22https://amberstudent.com/...
      const hrefPattern =
        /href\s*=\s*\\*(["'])(?:%22|&quot;|&#34;)*((?:https:\/\/(?:www\.)?)amberstudent\.com[^"'\\<>]*)(?:%22|&quot;|&#34;)*\\*\1/gi;

      return html.replace(hrefPattern, (match, quote, url) => {
        const path = url.replace(/^https:\/\/(?:www\.)?amberstudent\.com/i, "");
        if (path.includes("/blog")) {
          return `href=${quote}https://amberstudent.com${path}${quote}`;
        }
        return `href=${quote}https://amberstudent.cn${path}${quote}`;
      });
    } catch (error) {
      console.error("Error while replacing amber domain in links:", error);
      return html;
    }
  }

  // ==================Non-China====================
  if (currentLanguage === "en") return html;

  try {
    // Pattern 1: Absolute links to amberstudent.com
    // e.g. href="https://amberstudent.com/places/search/london"
    const absoluteHrefPattern =
      /(<a[^>]*\s+href=["'])(https:\/\/amberstudent\.com)(\/[^"']*)(["'][^>]*>)/gi;

    let result = html.replace(absoluteHrefPattern, (match, beforeHref, domain, path, afterHref) => {
      // Skip blog links — vernacular blog pages don't exist
      if (path.includes("/blog")) {
        return match;
      }
      const localizedPath = addLocaleToInternalPath(path, currentLanguage);
      return `${beforeHref}${domain}${localizedPath}${afterHref}`;
    });

    // Pattern 2: Relative links (starting with /)
    // e.g. href="/places/search/london"
    // Excludes links that already start with a language prefix or are absolute URLs
    const relativeHrefPattern = /(<a[^>]*\s+href=["'])(\/[^"']*)(["'][^>]*>)/gi;

    result = result.replace(relativeHrefPattern, (match, beforeHref, path, afterHref) => {
      // Skip blog links — vernacular blog pages don't exist
      if (path.includes("/blog")) {
        return match;
      }
      // Skip if already has a language prefix
      if (hasLanguagePrefix(path)) {
        return match;
      }
      const localizedPath = addLocaleToInternalPath(path, currentLanguage);
      return `${beforeHref}${localizedPath}${afterHref}`;
    });

    return result;
  } catch (error) {
    console.error("Error while adding locale prefix to links:", error);
    return html;
  }
}

export default replaceAmberDomainInLinks;
